<template>
  <v-container fluid pa-0 ma-0>
    <!--UPLOAD-->
    <v-flex xs12 class="dropbox">
      <form enctype="multipart/form-data" novalidate v-show="isOnUploadPage">
        <v-flex xs12>
          <input
            type="file"
            multiple
            @change="filesChange( $event.target.files); fileCount = $event.target.files.length"
            accept="image/*, application/pdf"
            class="input-file"
          />
        </v-flex>
        <v-layout column align-center justify-center>
          <v-flex xs10>
            <span>Choose the type of file that you are uploading.</span>
          </v-flex>
          <v-flex v-show="isInitial || isSuccess">
            <v-flex xs6>
              <v-select
                :items="itemFileType"
                v-model="fileType"
                label="Choose File Type"
                item-text="label"
                item-value="value"
                prepend-icon="cloud_upload"
              ></v-select>
            </v-flex>
            <v-flex xs12>
              <span>
                Then drag your pdf or image file(s) here to begin
                <br />or click to browse
                <br />
                <p v-show="isSaving">
                  Uploading {{ fileCount }} files...
                  <v-progress-linear :indeterminate="isSaving"></v-progress-linear>
                </p>
              </span>
            </v-flex>
          </v-flex>
        </v-layout>
      </form>
    </v-flex>

    <v-flex xs12 v-if="isDownloading">
      <span>Downloading</span>
      <v-progress-linear :indeterminate="true"></v-progress-linear>
    </v-flex>

    <v-flex xs12 v-if="isDeleting">
      <span>Deleting</span>
      <v-progress-linear :indeterminate="true"></v-progress-linear>
    </v-flex>

    <!--SUCCESS-->
    <v-flex xs12 v-if="isSuccess || rows.length > 0">
      <h2 v-if="isOnUploadPage">Uploaded {{ rows.length }} file(s) successfully.</h2>
      <strong>Click a button below to download individual files.</strong>
      <v-data-table :headers="headers" :items="rows" class="elevation-1" hide-actions>
        <template slot="items" slot-scope="props">
          <td class="text-xs-left">
            <v-btn flat icon color="green" @click="downloadAttachment(props.item)">
              <v-icon>cloud_download</v-icon>
            </v-btn>
          </td>
          <td class="text-xs-left">
            <b>{{ props.item.filename }}</b>
          </td>
          <td class="text-xs-left">{{ props.item.contentType }}</td>
          <td class="text-xs-left">{{ props.item.fileType }}</td>
          <td class="text-xs-left">
            <v-btn
              v-if="!isDeleting"
              flat
              icon
              color="red"
              @click="removeAttachment(props.item.fileGuid)"
            >
              <v-icon>cancel</v-icon>
              <v-progress-circular v-show="isDeleting" indeterminate color="red"></v-progress-circular>
            </v-btn>
          </td>
        </template>
      </v-data-table>
    </v-flex>
    <!--FAILED-->
    <v-flex xs12 v-if="isFailed">
      <h2>Uploaded failed.</h2>
      <p>
        <a href="javascript:void(0)" @click="reset()">Try again</a>
      </p>
      <pre>{{ uploadError }}</pre>
    </v-flex>
    <message-alert :component="message"></message-alert>
  </v-container>
</template>

<script>
import { mapActions, createNamespacedHelpers, mapGetters } from "vuex";
import {
  mapUploadFields,
  mapUploadMultiRowFields
} from "../store/modules/orders";
const { mapState: mapOrderState } = createNamespacedHelpers(`orders`);
const { mapActions: mapUploadActions } = createNamespacedHelpers(
  `orders/uploads`
);
import { httpError } from "../models/httpError";

const STATUS_INITIAL = 0,
  STATUS_SAVING = 1,
  STATUS_SUCCESS = 2,
  STATUS_FAILED = 3;

export default {
  name: "FileUploader",
  props: {
    orderGUID: String,
    isOnUploadPage: Boolean,
  },
  data () {
    return {
      uploadedFiles: [],
      uploadError: null,
      currentStatus: null,
      itemFileType: [
        { label: "General", value: "general" },
        { label: "Licence", value: "licence" },
        { label: "Business Card", value: "businessCard" },
        { label: "Bill", value: "bill" }
      ],
      fileCount: 0,
      fileType: this.$route.params.fileType,
      files: new FormData(),
      headers: [
        {
          text: "Download",
          align: "left",
          sortable: false,
          value: "true"
        },
        {
          text: "File Name",
          align: "left",
          sortable: true,
          value: "filename"
        },
        {
          text: "Content Type",
          align: "left",
          sortable: true,
          value: "contentType"
        },
        {
          text: "File Type",
          align: "left",
          sortable: true,
          value: "fileType"
        },
        {
          text: "Remove",
          align: "left",
          sortable: false,
          value: "true"
        }
      ],
      isDownloading: false,
      isDeleting: false
    };
  },
  watch: {
    isDownloading (val) {
      if (val) return setTimeout(() => (this.isDownloading = false), 1000); //Added a small delay just in case of super-quick downloads.
    },
    isDeleting (val) {
      if (val) return setTimeout(() => (this.isDeleting = false), 1000);
    }
  },
  computed: {
    ...mapUploadFields(["message"]),
    ...mapUploadMultiRowFields(["rows"]),
    ...mapGetters("orders", ["orderID"]),
    isInitial () {
      return this.currentStatus === STATUS_INITIAL;
    },
    isSaving () {
      return this.currentStatus === STATUS_SAVING;
    },
    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS;
    },
    isFailed () {
      return this.currentStatus === STATUS_FAILED;
    }
  },

  methods: {
    ...mapUploadActions([
      "populateUploads",
      "updateUploadsOnOrder",
      "removeRows",
      "setMessage",
      "UploadToKayako",
      "getAttachment",
      "deleteAttachment",
      "getExtension",
      "populateUploadsCustomer"
    ]),
    reset () {
      // reset form to initial state
      this.currentStatus = STATUS_INITIAL;
      this.uploadedFiles = [];
      this.uploadError = null;
      this.files = new FormData();
      this.fileCount = 0;
    },
    save () {
      // upload data to the server
      this.currentStatus = STATUS_SAVING;
      let vm = this;
      let form = { guid: vm.orderGUID, data: vm.files, fileType: vm.fileType };
      vm.updateUploadsOnOrder(form)
        .then(x => {
          vm.uploadedFiles = [].concat(x.files);
          vm.currentStatus = STATUS_SUCCESS;
          vm.currentStatus = STATUS_INITIAL;
        })
        .catch(err => {
          vm.uploadError = err.response;
          //vm.isFailed();
          //setTimeout(() => vm.currentStatus = STATUS_FAILED, 1000);
          vm.currentStatus = STATUS_FAILED;
        });
      vm.files = new FormData();
      
      //vm.uploadedFiles = [].concat(x.files);
    },
    downloadAttachment (item) {
      const vm = this;
      vm.isDownloading = true;
      let filetoBeDownloaded = { orderID: vm.orderID, file: item };
      vm.getAttachment(filetoBeDownloaded).then(x => {
        vm.downloadAttachment = false;
        vm.$forceUpdate();
      });
    },
    filesChange (fileList) {
      // handle file changes
      let vm = this;
      var allowed = [];
      if (!fileList.length) return;
      this.fileCount = fileList.length;
      // append the files to FormData

      Array.from(Array(fileList.length).keys()).map(x => {
        vm.files.append("files", fileList[x], fileList[x].name);
      });

      // save it
      this.save();
    },
    removeAttachment (fileGuid) {
      let vm = this;
      //
      this.isDeleting = true;

      let filetoBeDeleted = { orderID: vm.orderID, guid: fileGuid };
      this.deleteAttachment(filetoBeDeleted)
        .then(x => {
          this.isDeleting = false;
          this.reset();
          this.populateUploads(vm.orderID);
        })
        .catch(err => {
          vm.uploadError = err.response;
        });
    },
    setIcon (type) {
      if (type == "application/pdf" || type == "application/text") {
        return;
      } else {
        return "photo";
      }
    }
  },
  mounted () {
    this.reset();
    this.populateUploads(this.orderID);
  }
};
</script>

<style scoped>
.dropbox {
  outline: 2px dashed grey; /* the dash box */
  outline-offset: -10px;
  background: lightcyan;
  color: dimgray;
  padding: 10px 10px;
  min-height: 200px; /* minimum height */
  position: relative;
  cursor: pointer;
}

.input-file {
  opacity: 0; /* invisible but it's there! */
  width: 100%;
  height: 100%;
  position: absolute;
  cursor: pointer;
}

.dropbox:hover {
  background: lightblue; /* when mouse over to the drop zone, change color */
}

.dropbox p {
  font-size: 1.2em;
  text-align: center;
  padding: 50px 0;
}
</style>